import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  getEquipmentDetails,
  deactivateEquipment,
  activateEquipment,
  deleteEquipment,
  getEquipmentDeactivationDetails,
  getEquipmentDeletionDetails,
  getGuideBoatsListing,
  fetchBoatById,
  cleanBoat,
  updateBoat
} from "store/boats"
import { routes } from "router/routes"
import { toaster } from "components/common/Toast"
import { isMediaFileVideo } from "utils/media-upload"
import { useEquipmentModals } from "../../hooks/useEquipmentModals"
import { determineDeactivateMode } from "../../utils/determineDeactivateMode"

/**
 * Custom hook for managing all modal states and handlers in EquipmentDetails
 * @param {string} id - Equipment ID from URL params
 * @param {object} equipment - Mapped equipment data
 * @param {object} deactivationData - Deactivation details and loading states
 * @param {Function} t - Translation function
 * @returns {object} Modal states and handlers
 */
export const useEquipmentDetailsModals = (id, equipment, deactivationData, t) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Destructure deactivation data
  const {
    equipmentDeactivationDetails,
    equipmentDeletionDetails,
    deactivationDetailsLoading,
    deletionDetailsLoading,
    deactivateLoading,
    deleteLoading
  } = deactivationData

  // Use existing hook for deactivate/delete modal state
  const { deactivateDeleteModalState, openDeactivateModal, openDeleteModal, closeDeactivateDeleteModal, setDeactivateDeleteDetails } =
    useEquipmentModals()

  // Equipment modal state
  const [showEquipmentModal, setShowEquipmentModal] = useState(false)
  const [modalBoatId, setModalBoatId] = useState(null)
  const [isDuplicateMode, setIsDuplicateMode] = useState(false)

  // Video player state
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)

  // Assign experiences modal state
  const [showAssignExperiencesModal, setShowAssignExperiencesModal] = useState(false)

  // Remove experience confirmation state
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [experienceToRemove, setExperienceToRemove] = useState(null)
  const [experienceToRemoveName, setExperienceToRemoveName] = useState("")

  /**
   * Fetch boat data for modal when modal opens
   */
  useEffect(() => {
    if (modalBoatId && showEquipmentModal) {
      dispatch(fetchBoatById(modalBoatId))
    }
  }, [modalBoatId, showEquipmentModal, dispatch])

  /**
   * Fetch equipment deactivation details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeactivationDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "deactivate") {
      const mode = equipmentDeactivationDetails.mode || determineDeactivateMode(equipmentDeactivationDetails)
      setDeactivateDeleteDetails({
        ...equipmentDeactivationDetails,
        mode
      })
    }
  }, [equipmentDeactivationDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  /**
   * Fetch equipment deletion details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeletionDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "delete") {
      const mode = equipmentDeletionDetails.mode || determineDeactivateMode(equipmentDeletionDetails)
      setDeactivateDeleteDetails({
        ...equipmentDeletionDetails,
        mode
      })
    }
  }, [equipmentDeletionDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  // ============ Equipment Modal Handlers ============

  /**
   * Handle edit button click
   * Opens the edit equipment modal
   */
  const handleEdit = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    setIsDuplicateMode(false)
    setShowEquipmentModal(true)
    setModalBoatId(Number(id))
  }

  /**
   * Handle duplicate button click
   * Opens add equipment modal in duplicate mode
   */
  const handleDuplicate = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    setIsDuplicateMode(true)
    setShowEquipmentModal(true)
    setModalBoatId(Number(id))
  }

  /**
   * Handle modal close
   * Cleans up boat data and resets modal state
   */
  const handleCancelEquipmentModal = () => {
    setShowEquipmentModal(false)
    dispatch(cleanBoat())
    dispatch(getEquipmentDetails(id))
  }

  // ============ Video Player Handlers ============

  /**
   * Handle media click in gallery
   * Opens video player modal if media is a video
   */
  const handleMediaClick = (mediaItem) => {
    if (isMediaFileVideo(mediaItem)) {
      setSelectedMedia(mediaItem)
      setVideoPlayerOpen(true)
    }
  }

  /**
   * Handle closing video player modal
   */
  const handleCloseVideoPlayer = () => {
    setVideoPlayerOpen(false)
    setSelectedMedia(null)
  }

  // ============ Assign Experiences Handlers ============

  /**
   * Handle "Assign Experiences" button click
   */
  const handleAssignExperiences = () => {
    setShowAssignExperiencesModal(true)
  }

  /**
   * Handle assign experiences modal close
   */
  const handleAssignExperiencesModalClose = () => {
    setShowAssignExperiencesModal(false)
  }

  /**
   * Handle saving assigned experiences
   */
  const handleAssignExperiencesSave = async (selectedExperienceIds) => {
    const formData = new FormData()
    for (const tripId of selectedExperienceIds) {
      formData.append("boat[trip_ids][]", tripId)
    }
    if (selectedExperienceIds.length === 0) {
      formData.append("boat[trip_ids][]", "")
    }
    await dispatch(updateBoat(id, formData))
    await dispatch(getEquipmentDetails(id))
  }

  // ============ Remove Experience Handlers ============

  /**
   * Show confirmation modal before removing an experience
   */
  const handleRemoveExperience = (experienceIdToRemove) => {
    if (equipment?.trips?.length === 1) {
      return
    }

    const experienceToDelete = equipment?.trips?.find((trip) => trip.id === experienceIdToRemove)
    const experienceName = experienceToDelete?.title || "this experience"

    setExperienceToRemove(experienceIdToRemove)
    setExperienceToRemoveName(experienceName)
    setShowRemoveConfirmModal(true)
  }

  /**
   * Actually remove the experience after confirmation
   */
  const executeRemoveExperience = (experienceIdToRemove) => {
    const remainingTripIds = equipment?.trips?.filter((trip) => trip.id !== experienceIdToRemove)?.map((trip) => trip.id) || []

    const formData = new FormData()
    for (const tripId of remainingTripIds) {
      formData.append("boat[trip_ids][]", tripId)
    }

    if (remainingTripIds.length === 0) {
      formData.append("boat[trip_ids][]", "")
    }

    dispatch(updateBoat(id, formData))
      .then(() => {
        toaster.success(t("equipment.experience_removed_successfully", { defaultValue: "Experience removed successfully" }))
        dispatch(getEquipmentDetails(id))
      })
      .catch(() => {
        toaster.error(t("equipment.experience_remove_failed", { defaultValue: "Failed to remove experience" }))
      })
  }

  /**
   * Handle confirmation of experience removal
   */
  const handleConfirmRemove = () => {
    if (experienceToRemove) {
      executeRemoveExperience(experienceToRemove)
    }
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
  }

  /**
   * Handle cancellation of experience removal
   */
  const handleCancelRemove = () => {
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
  }

  // ============ Deactivate/Delete Handlers ============

  /**
   * Handle deactivate button click
   */
  const handleDeactivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    openDeactivateModal(equipmentForModal)

    dispatch(getEquipmentDeactivationDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle activate button click
   */
  const handleActivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    dispatch(activateEquipment(id))
      .then((response) => {
        const successMessage = response?.message || `${equipment.name} has been activated successfully`
        toaster.success(successMessage)
        dispatch(getEquipmentDetails(id))
        dispatch(getGuideBoatsListing())
      })
      .catch((error) => {
        console.error("Equipment activation failed:", error)
      })
  }

  /**
   * Handle delete button click
   */
  const handleDelete = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    openDeleteModal(equipmentForModal)

    dispatch(getEquipmentDeletionDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment deletion details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle deactivate/delete modal confirmation
   * @param {object} data - Additional data for the request
   */
  const handleDeactivateDeleteOk = async (data = {}) => {
    const { equipment: modalEquipment, action } = deactivateDeleteModalState
    const equipmentId = modalEquipment?.key || modalEquipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const actionFn = action === "deactivate" ? deactivateEquipment : deleteEquipment

    dispatch(actionFn(equipmentId, data))
      .then(() => {
        toaster.success(
          action === "deactivate"
            ? t("equipment.success.deactivated", { name: modalEquipment.title })
            : t("equipment.success.deleted", { name: modalEquipment.title })
        )
        closeDeactivateDeleteModal()
        dispatch(getGuideBoatsListing())

        if (action === "delete") {
          navigate(routes.guideResourcesPath())
        } else {
          dispatch(getEquipmentDetails(id))
        }
      })
      .catch((error) => {
        console.error("Equipment operation failed:", error)
      })
  }

  // ============ Navigation Handlers ============

  /**
   * Handle "Add New Booking" button click
   */
  const handleAddNewBooking = () => {
    navigate(routes.guideBookingNewPath(), {
      state: {
        filterExperienceIds: equipment?.trips?.map((trip) => trip.id) || [],
        equipmentId: equipment?.id,
        equipmentName: equipment?.name
      }
    })
  }

  /**
   * Handle dropdown menu item clicks
   */
  const handleMenuClick = ({ key }) => {
    switch (key) {
      case "duplicate":
        handleDuplicate()
        break
      case "deactivate":
        if (equipment?.is_active) {
          handleDeactivate()
        } else {
          handleActivate()
        }
        break
      case "delete":
        handleDelete()
        break
      default:
        break
    }
  }

  return {
    // Equipment Modal
    showEquipmentModal,
    modalBoatId,
    isDuplicateMode,
    handleEdit,
    handleDuplicate,
    handleCancelEquipmentModal,

    // Video Player
    videoPlayerOpen,
    selectedMedia,
    handleMediaClick,
    handleCloseVideoPlayer,

    // Assign Experiences
    showAssignExperiencesModal,
    handleAssignExperiences,
    handleAssignExperiencesModalClose,
    handleAssignExperiencesSave,

    // Remove Experience Confirmation
    showRemoveConfirmModal,
    experienceToRemoveName,
    handleRemoveExperience,
    handleConfirmRemove,
    handleCancelRemove,

    // Deactivate/Delete
    deactivateDeleteModalState,
    handleDeactivate,
    handleActivate,
    handleDelete,
    handleDeactivateDeleteOk,
    closeDeactivateDeleteModal,

    // Menu
    handleMenuClick,

    // Navigation
    handleAddNewBooking,

    // Loading states (pass through)
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  }
}
